import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import BlogNotFound from '../../components/BlogNotFound';
import { getBlogBySlug } from '../../data/blogs';

const Blog18032026 = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const blog = getBlogBySlug('are-your-skills-aiproof-navigating-the');

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1100);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [
    {
        "id": "context",
        "title": "1. Context",
        "originalTitle": "Context"
    },
    {
        "id": "problem-statement",
        "title": "2. Problem Statement",
        "originalTitle": "Problem Statement"
    },
    {
        "id": "core-framework-the-ai-driven-skill-transformation",
        "title": "3. Core Framework: The AI-Driven Skill Transformation",
        "originalTitle": "Core Framework: The AI-Driven Skill Transformation"
    },
    {
        "id": "comparative-analysis",
        "title": "4. Comparative Analysis",
        "originalTitle": "Comparative Analysis"
    },
    {
        "id": "business-use-cases",
        "title": "5. Business Use Cases",
        "originalTitle": "Business Use Cases"
    },
    {
        "id": "benefits-outcomes",
        "title": "6. Benefits & Outcomes",
        "originalTitle": "Benefits & Outcomes"
    },
    {
        "id": "challenges-realities",
        "title": "7. Challenges & Realities",
        "originalTitle": "Challenges & Realities"
    },
    {
        "id": "future-outlook-the-next-12-months",
        "title": "8. Future Outlook: The Next 12 Months",
        "originalTitle": "Future Outlook: The Next 12 Months"
    },
    {
        "id": "conclusion",
        "title": "9. Conclusion",
        "originalTitle": "Conclusion"
    },
    {
        "id": "call-to-action",
        "title": "10. Call to Action",
        "originalTitle": "Call to Action"
    }
];

  const faqData = [
    {
        "question": "What is this technology and how does it work?",
        "answer": "This technology represents a significant advancement in the field, offering innovative solutions to common challenges through modern approaches and proven methodologies."
    },
    {
        "question": "Who can benefit from implementing this solution?",
        "answer": "Organizations of all sizes can benefit, particularly those looking to improve efficiency, reduce costs, and enhance their competitive advantage through technological innovation."
    },
    {
        "question": "What are the main challenges in implementation?",
        "answer": "Key challenges include initial setup complexity, integration with existing systems, and ensuring proper training. However, with proper planning and support, these can be effectively managed."
    },
    {
        "question": "What ROI can be expected?",
        "answer": "While results vary by organization, typical implementations show significant improvements in operational efficiency, cost reduction, and enhanced capabilities within the first year."
    }
];

  const tldrPoints = [
    "The future of work is no longer a distant concept; it's a rapidly unfolding reality driven by Artificial Intelligence. This technological wave, distin...",
    "The accelerating adoption of AI presents a significant challenge to businesses and individuals alike, primarily through operational inefficiencies ste...",
    "AI-driven skill transformation refers to the dynamic process by which the capabilities required for job performance evolve in response to the integrat..."
];

  const keyTakeaways = [
    "Fast implementation strategies deliver measurable ROI within weeks, not months",
    "Agile methodologies reduce time-to-production by 60-80% compared to traditional approaches",
    "Cloud-native architecture enables rapid scaling without infrastructure bottlenecks",
    "Automated workflows eliminate manual bottlenecks and accelerate delivery timelines",
    "Real-time analytics provide immediate insights for faster decision-making"
];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "author": {
          "@type": "Organization",
          "name": "Agility"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "Are Your Skills AI-Proof? Navigating the Shifting Tides of Workforce Transformation",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": [".tldr-section", ".key-takeaways-section"]
        }
      },
      {
        "@type": "HowTo",
        "name": blog?.title,
        "description": blog?.excerpt,
        "step": keyTakeaways.map((takeaway, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": `Step ${index + 1}`,
          "text": takeaway
        }))
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return <BlogNotFound />;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.publishDate}</span>
              </div>
            </div>

            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: '#d1d5db',
              lineHeight: '1.75',
              marginBottom: '2rem'
            }}>
              {blog.excerpt}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {blog.tags.map((tag, index) => (
                <span key={index} style={{
                  padding: '0.25rem 0.75rem',
                  background: '#121212',
                  color: '#14b8a6',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #262626'
                }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', minHeight: '100vh', gap: '3rem', flexDirection: isDesktop ? 'row' : 'column' }}>
          {/* Sidebar TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              alignSelf: 'flex-start',
              zIndex: 999
            }}>
              <h3 style={{
                color: '#ffffff',
                fontWeight: '600',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Table of Contents</h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.2s',
                      borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                      background: activeSection === item.id ? 'rgba(20,184,166,0.15)' : 'transparent',
                      color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.background = '#121212';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#9ca3af';
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{
              width: '100%',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #262626',
                padding: '1rem'
              }}>
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <span>Table of Contents</span>
                  <ChevronDown style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {tocOpen && (
                  <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: 'none',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          )}

          {/* Article Content */}
          <div style={{ flex: 1 }}>
            <article style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>

              {/* TL;DR Section - AEO Optimized */}
              <div className="tldr-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>TL;DR</span>
                  Quick Summary
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {tldrPoints.map((point, idx) => (
                    <li key={idx} style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.25rem',
                        lineHeight: '1'
                      }}>•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              
              <h2 id="context" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Context
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The future of work is no longer a distant concept; it's a rapidly unfolding reality driven by Artificial Intelligence. This technological wave, distinct from previous automation trends, is fundamentally reshaping the global job market at an unprecedented pace. While earlier automation primarily targeted repetitive physical labor, the rise of Large Language Models (LLMs) and advanced AI agents now directly impacts tasks traditionally requiring significant human expertise: drafting, data analysis, summarization, and complex decision support. Companies are already running internal pilots where AI handles 60-80% of junior-level work, a clear signal of the swift transformation underway before any public headcount announcements are made.
              </p>

              <h2 id="problem-statement" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Problem Statement
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The accelerating adoption of AI presents a significant challenge to businesses and individuals alike, primarily through operational inefficiencies stemming from skill gaps and escalating costs associated with workforce adaptation. A recent McKinsey Global Institute report highlights that 57% of U.S. work hours could be automated with existing technologies, with 40% of U.S. jobs falling into a "highly automatable" category. This isn't a future prediction; it's a measurement of current technical feasibility. The World Economic Forum (WEF) projects that 39% of existing skill sets will become outdated between 2025 and 2030, emphasizing the urgency of reskilling and upskilling initiatives. Failure to address this swiftly can lead to a workforce unable to leverage new AI tools, increased operational costs from human-AI friction, and a critical talent deficit in emerging "AI-native" roles.
              </p>

              <h2 id="core-framework-the-ai-driven-skill-transformation" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Core Framework: The AI-Driven Skill Transformation
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                AI-driven skill transformation refers to the dynamic process by which the capabilities required for job performance evolve in response to the integration of artificial intelligence technologies into workflows. It encompasses both the displacement of tasks traditionally performed by humans and the creation of new roles and demands for advanced human-AI collaboration skills.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                AI's impact on work is characterized by "automation and augmentation." AI agents and robots are increasingly capable of handling multi-step processes autonomously, allowing entire workflows to be redesigned around human-agent partnerships. For instance, in sales, an AI agent can manage research, lead qualification, initial outreach, and scheduling, leaving high-stakes human interaction, negotiation, and closing to human professionals. This shift is reflected in trends like a 35% decline in entry-level job postings since January 2023, and a 13% reduction in junior hiring by U.S. companies adopting AI. Conversely, AI also augments human abilities, enabling employees to focus on higher-value, strategic tasks by offloading repetitive duties.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                While AI's capabilities are vast, they are not limitless. Roles requiring deep human qualities like empathy, complex judgment, and unique dexterity remain highly resistant to full automation. McKinsey notes that professions such as teaching, nursing, and certain sales functions will retain a strong human core. Even in highly exposed fields like radiology, human oversight and critical decision-making are still essential. The bottleneck often isn't AI's technical capacity, but rather the practical challenges of deployment, workflow redesign, and effective change management within organizations.
              </p>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Core Framework: The AI-Driven Skill Transformation
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/5668869/pexels-photo-5668869.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Core Framework: The AI-Driven Skill Transformation"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of core framework: the ai-driven skill transformation concepts and implementation strategies.
                </p>
              </div>

              <h2 id="comparative-analysis" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Comparative Analysis
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The current AI-driven transformation differs significantly from previous industrial and digital revolutions.
              </p>

              <div style={{
                overflowX: 'auto',
                margin: '2rem 0',
                border: '1px solid #262626',
                borderRadius: '8px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'clamp(0.875rem, 1.8vw, 1rem)',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  <thead>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Feature
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Previous Automation Waves (e.g., Industrial Revolution, Early IT)
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Current AI-Driven Transformation (Generative AI &amp; LLMs)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Primary Target
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Repetitive physical labor, routine data entry
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Cognitive tasks: drafting, analysis, summarization, decision support, knowledge work
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Impacted Workforce
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Blue-collar workers, clerical staff
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Knowledge workers, mid-level professionals, entry-level white-collar roles
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Pace of Change
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Gradual over decades
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Rapid; significant shifts observed in 2-3 years
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Skill Bias
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Displaced lower-skilled workers
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        "Reverse skill bias": disproportionately impacts higher-educated knowledge workers
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Nature of Change
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Task-specific automation, process efficiency
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Workflow transformation, human-AI partnerships, new operating models
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Job Creation/Loss
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Net job creation, but often requiring different physical skills
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        WEF predicts 170M new jobs vs. 92M displaced by 2030, a net increase
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 id="business-use-cases" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Business Use Cases
              </h2>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Manual data analysis, report generation, and compliance checks are time-consuming and prone to error, leading to operational inefficiencies and increased costs.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: AI can automate 60-80% of data analysis for financial reports, flag compliance risks with 95% accuracy, and generate initial drafts of client communications 10x faster. This frees up financial analysts for strategic planning and complex problem-solving. McKinsey estimates generative AI could reduce human-serviced customer contacts by up to 50% in banking.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: High demand for personalized content, market research, and campaign optimization drains resources and limits scalability.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Generative AI can produce marketing copy, analyze customer sentiment, and identify emerging trends. Companies can see a 30% reduction in content creation costs and a 20% increase in campaign effectiveness by leveraging AI for initial drafts and data-driven insights. This allows human marketers to focus on creative strategy and brand storytelling.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Extensive time spent on document review, legal research, and contract analysis, leading to high costs and slower case progression.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: AI tools can review legal documents 100x faster than humans, identify relevant precedents with 90% accuracy, and summarize complex legal texts in minutes. This allows paralegals and lawyers to dedicate more time to strategic legal arguments, client interaction, and intricate case details, potentially saving firms millions in labor costs.
              </li>
              </ul>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Business Use Cases
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Business Use Cases"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of business use cases concepts and implementation strategies.
                </p>
              </div>

              <h2 id="benefits-outcomes" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Benefits & Outcomes
              </h2>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Enhanced Efficiency: AI automates routine, data-intensive tasks, speeding up processes significantly. For example, AI systems solved 71.7% of coding benchmarks in 2024, up from 4.4% in 2023.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Improved Accuracy: AI minimizes human error in data processing, analysis, and rule-based operations.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Scalability: AI solutions can scale rapidly to handle large volumes of work without proportional increases in human resources.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Advanced Analytics: AI uncovers deeper insights from vast datasets, leading to more informed decision-making.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Cost Reduction: Automation of tasks can lead to significant operational cost savings. Over a third (37%) of companies reported that AI replaced certain workers' jobs in 2023 because "they were no longer needed".
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Increased Productivity: Workers, augmented by AI, can achieve higher output and focus on high-value activities. One documented case study saw a company with 5,000 customer service agents achieve a 14% increase in issue resolution per hour.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Competitive Advantage: Early adopters who effectively integrate AI and reskill their workforce can gain a significant lead in their respective markets. The WEF estimates that AI will transform 86% of businesses by 2030.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Innovation & New Opportunities: AI creates entirely new roles and business models, fostering innovation. The WEF forecasts 170 million new jobs created globally by 2030.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Enhanced Employee Experience: By taking over monotonous tasks, AI can enable employees to engage in more stimulating and impactful work, potentially reducing burnout.
              </li>
              </ul>

              <h2 id="challenges-realities" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Challenges & Realities
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Implementing AI-driven workforce transformation is not without its hurdles. The complexity lies in managing not just the technology but also the human element. Resistance to change from employees fearing job displacement is a major factor, with 30% of US workers concerned about being replaced by AI. Bridging the skills gap requires substantial investment in continuous learning and development; 85% of employers plan to prioritize workforce upskilling. Furthermore, integrating AI effectively demands a complete redesign of workflows, not just isolated task automation, which can be a complex and time-consuming organizational effort. Ethical considerations around AI bias, data privacy, and job security also add layers of complexity that must be addressed proactively.
              </p>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Challenges & Realities
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Challenges & Realities"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of challenges & realities concepts and implementation strategies.
                </p>
              </div>

              <h2 id="future-outlook-the-next-12-months" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Future Outlook: The Next 12 Months
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Over the next 12 months, the trend of AI integration will only accelerate, solidifying its role from "experimentation to integration." We anticipate a sharper focus on developing "AI fluency" the ability to direct, evaluate, and collaborate with AI tools effectively. LinkedIn reported a 21x increase in job adverts mentioning ChatGPT or GPT over the year to November 2023, indicating this shift. Organizations will continue to pilot and scale AI agent implementations, optimizing headcount in exposed categories. Demand for professionals skilled in Generative AI, prompt engineering, data analysis, cybersecurity, and cloud computing will surge, alongside critical soft skills like adaptability, communication, and emotional intelligence, which are expected to grow by 24% by 2030. Companies prioritizing workforce reskilling (with 80% planning AI training for workers) and actively transitioning staff into growing roles will be best positioned to thrive.
              </p>

              <h2 id="conclusion" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Conclusion
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                AI-driven workforce displacement is a tangible force, transforming job roles and skill requirements across the global economy. While posing significant challenges through automation of cognitive tasks, it simultaneously unlocks immense opportunities for efficiency, innovation, and the creation of new, more strategic roles. The key to navigating this era lies in proactive skill development, strategic organizational redesign, and a commitment to fostering a symbiotic partnership between human talent and advanced AI. Those who embrace this transformation with foresight and agility will not only future-proof their careers and businesses but also drive unprecedented growth and societal value.
              </p>

              <h2 id="call-to-action" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Call to Action
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Is your organization prepared for the AI-driven skill transformation? Don't get left behind. Contact us today for a Proof of Concept (POC) or a consultation to assess your current workforce capabilities and develop a bespoke strategy for thriving in the age of AI. Let's build your future-ready workforce together.
              </p>


              {/* Key Takeaways Section - AEO Optimized */}
              <div className="key-takeaways-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginTop: '3rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>⚡</span>
                  Key Takeaways - Fast Implementation Insights
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {keyTakeaways.map((takeaway, idx) => (
                    <li key={idx} style={{
                      color: '#ffffff',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(20, 184, 166, 0.1)'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.125rem',
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(20, 184, 166, 0.2)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        flexShrink: 0
                      }}>{idx + 1}</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Frequently Asked Questions
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '3rem' }}>
                {faqData.map((faq, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #262626',
                    transition: 'border-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#262626'}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{ color: '#14b8a6', flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.question}</span>
                    </h3>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                      lineHeight: '1.75',
                      marginLeft: '2rem',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div style={{
                marginTop: '3rem',
                background: '#121212',
                borderRadius: '16px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: '1px solid #262626',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '384px',
                  height: '384px',
                  background: 'rgba(20, 184, 166, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '1rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>
                    Ready to Transform Your Business?
                  </h3>
                  <p style={{
                    color: '#e5e7eb',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px',
                    margin: '0 auto 1.5rem',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Contact us today for a personalized consultation and discover how we can help you achieve your goals.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-block',
                      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                      border: '2px solid #14b8a6',
                      color: '#14b8a6',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#14b8a6';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#14b8a6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Get Started Today
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 'clamp(1.5rem, 4vw, 2rem)',
            right: 'clamp(1.5rem, 4vw, 2rem)',
            width: 'clamp(44px, 10vw, 48px)',
            height: 'clamp(44px, 10vw, 48px)',
            background: '#121212',
            border: '2px solid #262626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#14b8a6',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 50
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#14b8a6';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Blog18032026;
